<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
            <div class="list-view-heading__controls-end">
              <b-button variant="link"
                        class="list-view-icon-btn"
                        :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom
                        :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
              <b-dropdown right
                          no-caret
                          variant="link"
                          boundary="window"
                          toggle-class="list-view-icon-btn list-view-icon-btn--primary"
                          v-b-tooltip.hover.bottom
                          title="Agregar Proveedores">
                <template #button-content>
                  <AppIcon name="add" label="Agregar Proveedores" />
                </template>
                <b-dropdown-item @click="add()"><AppIcon name="add" /> Agregar Proveedor</b-dropdown-item>
                <b-dropdown-item @click="openImportCSV()"><AppIcon name="file-earmark-spreadsheet" /> Gestor de Importaciones</b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </section>

        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="flex: 1 1 260px; max-width: 340px;">
              <label class="filter-label">Buscar</label>
              <b-form-input type="text" size="sm" placeholder="Código, nombre, CUIT, teléfono o email" v-model="filters.name" v-on:keyup.enter.native="filterSuppliers"></b-form-input>
            </div>
            <div class="list-view-filter" style="flex: 1 1 190px; max-width: 260px;">
              <label class="filter-label">Estado</label>
              <v-select :options="arr.filter.status" v-model="filters.status" placeholder="Todos" :multiple="true" :select-on-tab="true"></v-select>
            </div>
            <b-button variant="outline-dark" size="sm" @click="filterSuppliers()" v-b-tooltip.hover title="Aplicar filtros" style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>
      </b-card>

      <b-card header-tag="header" footer-tag="footer">
        <b-row>
          <b-col>
            <b-table class="app-table"
                    ref="table"
                    responsive
                    :stacked="isMobileViewport ? 'md' : false"
                    sticky-header="60vh"
                    :hover="true"
                    :small="true"
                    :fixed="true"
                    :items="table.items"
                    :fields="table.fields"
                    :current-page="table.currentPage"
                    :per-page="table.perPage"
                    @row-clicked="onRowClicked"
                    :tbody-tr-class="rowClass"
                    :busy="table.isBusy"
                    v-if="table.items.length || table.isBusy">

                <template v-slot:table-colgroup="scope">
                  <col
                    v-for="field in scope.fields"
                    :key="field.key"
                    :style="{ width: field.width }"
                  >
                </template>

                <template v-slot:table-busy>
                  <div class="text-center text-danger my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong>Cargando...</strong>
                  </div>
                </template>

                <template v-slot:cell(f_users)="data">
                  <span v-b-tooltip.hover :title="usersTooltip(data.item.users)">
                    <AppIcon name="people" :font-scale="1.3" class="mr-1 align-middle" :class="data.item.users.length ? 'text-success' : 'text-muted'" />
                    <b-badge :variant="data.item.users.length ? 'success' : 'secondary'" pill>{{data.item.users.length}}</b-badge>
                    <span class="ml-1 text-muted small">{{data.item.users.length == 1 ? 'usuario' : 'usuarios'}}</span>
                  </span>
                </template>

                <template v-slot:cell(id)="data">
                  <b>{{ data.item.id }}</b>
                </template>

                <template v-slot:cell(code)="data">
                  <b>{{ data.item.code }}</b>
                </template>

                <template v-slot:cell(name)="data">
                  <b-link variant="link" @click="goSuppliersView(data.item)">
                    <b>{{data.item.name}}</b>
                  </b-link>
                </template>

                <template v-slot:cell(image)="data">
                  <b-avatar :src="data.item.image" v-if="data.item.image"></b-avatar>
                  <b-avatar v-else
                            :text="avatarFromName(data.item.name).text"
                            :style="avatarFromName(data.item.name).style"></b-avatar>
                  <span v-if="isMobileViewport" class="ml-2">{{data.item.name}}</span>
                </template>

                <template v-slot:cell(active)="data">
                  <div v-if="data.item.active">
                    <b-badge variant="success">Activo</b-badge>
                  </div>
                  <div v-else>
                    <b-badge variant="danger">Inactivo</b-badge>
                  </div>
                </template>

                <template v-slot:cell(shipping)="data">
                  <div v-if="data.item.shipping">
                    <b-badge variant="dark">SI</b-badge>
                  </div>
                </template>

                <template v-slot:cell(f_action)="data">
                  <b-dropdown right
                              no-caret
                              size="sm"
                              variant="link"
                              boundary="window"
                              toggle-class="app-table-action-toggle"
                              class="pull-right">
                    <template #button-content>
                      <AppIcon name="more_vert" label="Acciones" />
                    </template>

                    <b-dropdown-item @click="openSub(data.item)">
                      <AppIcon name="visibility" /> Visualizar
                    </b-dropdown-item>

                    <b-dropdown-header>Acciones</b-dropdown-header>
                    <b-dropdown-item @click="edit(data.item)" v-if="access.elements.editSuppliers">
                      <AppIcon name="edit" /> Editar
                    </b-dropdown-item>
                    <b-dropdown-item @click="remove(data.item)" v-if="access.elements.deleteSuppliers">
                      <AppIcon name="delete" class="text-danger" /> Eliminar
                    </b-dropdown-item>
                  </b-dropdown>
                </template>
            </b-table>
            <EmptyState v-else icon="people" title="No se encontraron registros" />

            <div class="app-table-footer">
              <span class="list-view-toolbar__count" v-if="table.tablePaginate">{{ table.tablePaginate.total }} registros</span>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <!-- DRAWER: detalle del proveedor seleccionado -->
      <b-sidebar
          id="sidebar-supplier-detail"
          v-model="detailDrawer"
          right
          shadow
          backdrop
          title="Detalle del Proveedor"
          width="420px"
          @hidden="hideSub">
        <div class="list-view-drawer" v-if="itemSelected">
          <div class="list-view-drawer__header">
            <div class="list-view-drawer__title">
              <span class="list-view-drawer__code">{{itemSelected.code ? itemSelected.code : '#' + itemSelected.id}}</span>
              <div>{{itemSelected.name}}</div>
              <div class="list-view-drawer__meta" v-if="itemSelected.email || itemSelected.phone">
                <span v-if="itemSelected.email">{{itemSelected.email}}</span>
                <span v-if="itemSelected.email && itemSelected.phone"> · </span>
                <span v-if="itemSelected.phone">{{itemSelected.phone}}</span>
              </div>
            </div>
            <div class="list-view-drawer__nav">
              <b-button size="sm"
                        variant="outline-secondary"
                        :disabled="!canGoPrevSupplier"
                        v-b-tooltip.hover
                        title="Proveedor anterior"
                        @click="goPrevSupplier()">
                <AppIcon name="chevron-up" />
              </b-button>
              <b-button size="sm"
                        variant="outline-secondary"
                        :disabled="!canGoNextSupplier"
                        v-b-tooltip.hover
                        title="Proveedor siguiente"
                        @click="goNextSupplier()">
                <AppIcon name="chevron-down" />
              </b-button>
            </div>
          </div>

          <div class="list-view-drawer__stats">
            <div class="list-view-drawer__stat" :class="{ 'list-view-drawer__stat--warning': !itemSelected.active }">
              <span class="list-view-drawer__stat-label">Estado</span>
              <span class="list-view-drawer__stat-value">{{itemSelected.active ? 'Activo' : 'Inactivo'}}</span>
            </div>
            <div class="list-view-drawer__stat">
              <span class="list-view-drawer__stat-label">Usuarios</span>
              <span class="list-view-drawer__stat-value">{{tableUsers.items.length}}</span>
            </div>
          </div>

          <div>
            <div class="list-view-drawer__section-title">
              <AppIcon name="people" /> Usuarios
            </div>

            <b-button @click="createUser(itemSelected)" type="button" variant="outline-dark" size="sm" class="float-right ml-2 mb-2" v-b-tooltip.hover title="Agregar Usuario de Proveedor">
              <AppIcon name="add" /> Agregar
            </b-button>
            <b-button @click="openFindUser(itemSelected)" type="button" variant="outline-secondary" size="sm" class="float-right ml-2 mb-2" v-b-tooltip.hover title="Buscar Usuario">
              <AppIcon name="search" />
            </b-button>

            <b-table class="app-table app-table--compact"
                responsive
                stacked
                :hover="true"
                :small="true"
                :fixed="false"
                :items="tableUsers.items"
                :fields="tableUsers.fields"
                v-if="tableUsers.items.length">

              <template v-slot:cell(roles)="row">
                <div v-if="row.item">
                  <div v-if="row.item.roles">
                    <b-badge  variant="secondary"
                              class="mr-2 badge-module-suppliers-custom"
                              v-for="value in filtroProveedor(row.item.roles)"
                              :key="value.id">
                      {{value.name}}
                    </b-badge>
                  </div>
                </div>
              </template>

              <template v-slot:cell(password_visible)="row">
                <b-button size="sm" variant="outline-secondary" @click="viewAccess(row.item)">
                  <AppIcon name="visibility" class="mr-1" /> Ver contraseña
                </b-button>
              </template>

              <template v-slot:cell(f_action)="row">
                <b-dropdown no-caret size="sm" variant="outline-secondary" boundary="window">
                  <template #button-content>
                    <AppIcon name="more_vert" label="Opciones" class="mr-1" /> Opciones
                  </template>
                  <b-dropdown-item @click="editRolesUser(row.item)">
                    <AppIcon name="edit" /> Editar Roles
                  </b-dropdown-item>
                  <b-dropdown-item @click="removeUser(row.item)">
                    <AppIcon name="delete" class="text-danger" /> Eliminar Usuario
                  </b-dropdown-item>
                </b-dropdown>
              </template>

            </b-table>
            <EmptyState v-else icon="people" title="No hay usuarios asignados" />
          </div>

          <b-button block
                    size="sm"
                    variant="outline-secondary"
                    class="text-left"
                    @click="goSuppliersView(itemSelected)">
            <AppIcon name="visibility" class="mr-1" /> Ver detalle completo
          </b-button>

          <div class="list-view-drawer__actions">
            <b-button size="sm" variant="outline-secondary" @click="edit(itemSelected)">
              <AppIcon name="edit" class="mr-1" /> Editar
            </b-button>
          </div>
        </div>

        <template #footer>
          <div class="list-view-drawer__footer" v-if="itemSelected && access.elements.deleteSuppliers">
            <b-button size="sm" variant="outline-danger" block @click="remove(itemSelected)">
              <AppIcon name="delete" class="mr-1" /> Eliminar
            </b-button>
          </div>
        </template>
      </b-sidebar>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon name="back" />
            Volver
          </b-button>
          <nav v-if="table.tablePaginate">
            <b-pagination class="pull-right mb-0"
                          size="sm"
                          pills=""
                          :total-rows="table.tablePaginate.total"
                          :per-page="table.tablePaginate.per_page"
                          v-model="table.currentPage"
                          @input="filterSuppliers()" />
          </nav>
        </div>
      </b-card>

      <!-- ########################### -->
      <!-- #####     MODALES     ##### -->
      <!-- ########################### -->

      <!-- CRUD MAIN -->
      <b-modal v-model="modal.form.active"
              :key="modal.form._id"
              :id="modal.form._id">
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="edit" /></span>
          <h5 class="app-modal-header__title">{{this.modal.form.title}}</h5>
        </div>

        <b-tabs active-nav-item-class="font-weight-bold text-uppercase text-dark">
          <b-tab>
            <template #title><AppIcon name="person" class="mr-1" /> General</template>
            <b-row>
              <b-col md="5">
                <b-form-group label="Logo">
                  <Imagen :images="crud.form.image" @load-image="crud.form.image = $event" typeImage="Logo"/>
                </b-form-group>
              </b-col>
              <b-col md="7">
                <b-row>
                  <b-col md="12">
                    <b-form-checkbox v-model="crud.form.active"
                                      switch
                                      size="sm"
                                      class="mt-4 pull-right">
                      Activo
                    </b-form-checkbox>
                  </b-col>
                  <b-col md="12">
                    <b-form-group label="Código">
                      <b-form-input type="text"
                                    size="sm"
                                    v-model="crud.form.code"
                                    required
                                    placeholder="Ingresar un código">
                      </b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col md="12">
                    <b-form-group label="Nombre">
                      <b-form-input type="text"
                                    size="sm"
                                    v-model="crud.form.name"
                                    required
                                    placeholder="Ingresar un nombre">
                      </b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-col>
              <b-col md="12">
                <b-form-group label="Email">
                  <b-form-input type="text"
                                size="sm"
                                v-model="crud.form.email"
                                required
                                placeholder="Ingresar un email">
                  </b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="12">
                <b-form-group label="Teléfono" description="Ej: 5493416908870 (13 dígitos)">
                  <b-form-input type="text"
                                size="sm"
                                v-model="crud.form.phone"
                                required>
                  </b-form-input>
                </b-form-group>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab>
            <template #title><AppIcon name="chat-square-text" class="mr-1" /> Observaciones</template>
            <b-row>
              <b-col md="12">
                <b-form-checkbox v-model="crud.form.shipping"
                                  switch
                                  size="sm"
                                  class="mb-3 pull-left">
                  ¿Es Transportista?
                </b-form-checkbox>
              </b-col>
              <b-col md="12">
                <b-form-group label="Observaciones">
                  <app-textarea
                    v-model="crud.form.observations"
                    placeholder="Ingresar una observación..."
                    rows="3"
                    size="sm"
                  ></app-textarea>
                </b-form-group>
              </b-col>
            </b-row>
          </b-tab>
        </b-tabs>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.form.active=false">Cancelar</b-button>
          <b-button variant="dark" @click="save()"><AppIcon name="save" class="mr-1" />Guardar</b-button>
        </div>
      </b-modal>

      <!-- CRUD USER -->
      <b-modal v-model="modal.formUser.active">
        <div slot="modal-header" class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="person" /></span>
          <h5 class="app-modal-header__title">{{this.modal.formUser.title}}</h5>
        </div>

        <b-row>
          <b-col>
            <b-form-group label="Nombre">
              <b-form-input type="text"
                            size="sm"
                            v-model="crud.formUser.name"
                            required
                            placeholder="Ingresar un nombre">
              </b-form-input>
            </b-form-group>
            <b-form-group label="Email">
              <b-form-input type="email"
                            size="sm"
                            v-model="crud.formUser.email"
                            required
                            placeholder="Ingresar un email">
              </b-form-input>
            </b-form-group>
            <b-form-group label="Contraseña">
              <b-form-input type="text"
                            size="sm"
                            v-model="crud.formUser.password"
                            required
                            placeholder="Ingresar una contraseña">
              </b-form-input>
            </b-form-group>

            <b-form-group label="Roles">
              <b-form-select v-model="crud.formUser.roles" :options="arr.roles" multiple :select-size="4"></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.formUser.active=false">Cancelar</b-button>
          <b-button variant="dark" @click="saveUser()"><AppIcon name="save" class="mr-1" />Guardar</b-button>
        </div>
      </b-modal>

      <!-- ROLES USER -->
      <b-modal v-model="modal.formUserRoles.active">
        <div slot="modal-header" class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="shield-check" /></span>
          <h5 class="app-modal-header__title">{{this.modal.formUserRoles.title}}</h5>
        </div>

        <b-row>
          <b-col>
            <b-form-group label="Nombre">
              <b-form-input type="text"
                            size="sm"
                            v-model="crud.formUser.name"
                            required
                            placeholder="Ingresar un nombre">
              </b-form-input>
            </b-form-group>
            <b-form-group label="Roles">
              <b-form-select v-model="crud.formUser.roles" :options="arr.roles" multiple :select-size="4"></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.formUserRoles.active=false">Cancelar</b-button>
          <b-button variant="dark" @click="saveUserRoles()"><AppIcon name="save" class="mr-1" />Guardar</b-button>
        </div>
      </b-modal>

      <!-- FIND USER -->
      <b-modal v-model="modal.formUserFind.active" size="lg">
        <div slot="modal-header" class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="search" /></span>
          <h5 class="app-modal-header__title">{{this.modal.formUserFind.title}}</h5>
        </div>

        <b-row>
          <b-col>
            <b-input-group>
              <b-form-input type="text"
                            size="sm"
                            v-model="crud.formUserFind.email"
                            required
                            placeholder="Ingresar un nombre o email de usuario"
                            v-on:keyup.enter.native="findUser()">
              </b-form-input>
              <b-input-group-append>
                <b-button variant="dark" size="sm" @click="findUser()">
                  <AppIcon name="search" />
                  Buscar
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-col>
        </b-row>
        <b-row class="mt-2">
          <b-col>
            <b-table class="mb-0 app-table app-table--compact"
                    responsive="sm"
                    :hover="true"
                    :small="true"
                    :fixed="false"
                    :items="tableUsersFind.items"
                    :fields="tableUsersFind.fields"
                    v-if="tableUsersFind.items.length">

              <template v-slot:cell(f_action)="row">
                <b-button variant="dark" size="sm" v-b-tooltip.hover title="Vincular este usuario al proveedor" @click="selectUser(row.item)">
                  <AppIcon name="person_add" class="mr-1" /> Seleccionar
                </b-button>
              </template>

            </b-table>
            <EmptyState v-if="modal.formUserFind.alertResult" icon="search" title="No se encontraron usuarios" />
          </b-col>
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.formUserFind.active=false">Cancelar</b-button>
        </div>
      </b-modal>

      <!-- PASSWORD -->
      <b-modal v-model="modal.password.active">
        <div slot="modal-header" class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="visibility" /></span>
          <h5 class="app-modal-header__title">{{this.modal.password.title}}</h5>
        </div>

        <b-row>
          <b-col>

            <b-input-group class="w-100 mb-1">
              <b-input-group-append>
                <div class="input-group-text">
                  <AppIcon name="person-fill" />
                </div>
              </b-input-group-append>
              <b-form-input  type="text"
                            size="md"
                            v-model="crud.password.email"
                            readonly>
              </b-form-input>
            </b-input-group>

            <b-input-group class="w-100">
              <b-form-input :type="crud.formUser.typePassword"
                            size="md"
                            v-model="crud.password.password"
                            readonly>
              </b-form-input>
              <b-input-group-append>
                <b-button variant="secondary" size="sm" @click="changeTypePassword()">
                  <AppIcon :name="crud.formUser.iconPassword" />
                </b-button>
              </b-input-group-append>
            </b-input-group>

          </b-col>
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="pull-right" @click="modal.password.active=false">Cerrar</b-button>
        </div>
      </b-modal>

      <!-- CRUD IMPORTACIONES -->
      <b-modal  v-model="modal.formImport.active"
                size="lg"
                :key="modal.formImport._id"
                :id="modal.formImport._id">
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="file-earmark-spreadsheet" /></span>
          <h5 class="app-modal-header__title">{{this.modal.formImport.title}}</h5>
        </div>

        <b-tabs pills card vertical>
          <b-tab @click="crud.formImport.type='import_store_suppliers'" active>
            <template #title><AppIcon name="file-earmark-spreadsheet" class="mr-1" /> Nuevos Proveedores</template>
            <b-card-text>
              <b-row>
                <b-col md="12" class="mb-2">
                  <b-alert show variant="info">
                      <h4 class="alert-heading">Importación de Proveedores</h4>

                      <div>
                        <AppIcon name="arrow-right" class="mr-1" /> El sistema importará todos los registros cargados en el archivo.
                      </div>
                      <div>
                        <AppIcon name="arrow-right" class="mr-1" /> Esta importación solo permite agregar <b>proveedores</b>.
                      </div>
                      <div>
                        <AppIcon name="arrow-right" class="mr-1" /> Si el <b>código y/o nombre</b> ya existe en la base de datos, se omite dicho registro.
                      </div>
                      <div>
                        <AppIcon name="arrow-right" class="mr-1" /> Las <b>condición fiscal, el tipo de documento, el punto de venta y la condición de pago</b>, sino existe omite la carga de dicho dato.
                      </div>

                      <hr>

                      <div class="mb-0">
                        Descargue el <a href="javascript:void(0);" @click="goDownloadFormat('examples-imports/suppliers/csv-format-suppliers.csv')"><b>formato de ejemplo</b></a> para esta importación.
                      </div>
                    </b-alert>
                </b-col>
                <b-col md="12" class="mb-2">
                  <b-form-group label="Archivo CSV [Proveedores]">
                    <b-form-file v-model="crud.formImport.file"
                                  size="sm"
                                  :state="Boolean(crud.formImport.file)"
                                  placeholder="Elija un archivo o sueltelo aquí..."
                                  drop-placeholder="Suelta el archivo aquí...">
                    </b-form-file>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card-text>
          </b-tab>
        </b-tabs>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.formImport.active=false">Cancelar</b-button>
          <b-button variant="dark" @click="importCSV()"><AppIcon name="file-earmark-spreadsheet" class="mr-1" />Importar</b-button>
        </div>

      </b-modal>
    </b-col>
  </b-row>
</template>
<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Session from '@/handler/session'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import Imagen from '@/components/inc/image/image'
  import Param from '@/config/parameters'
  import Funciones from '@/handler/funciones'
  import StorageLocal from '@/handler/storageLocal'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'

  export default {
    components: {
      Imagen,
      TabHeaderActions,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.PROVEEDORES,
          profile_id: Profiles.PERSONAL,
          view_reference: 'crud',
          elements: {
            editSuppliers: true,
            deleteSuppliers: true
          }
        },
        parameters: {
          codeSuppliers: Helper.hasParametersAccess(Param.P3)
        },
        table : {
          items: [],
          fields: [],
          currentPage: 1,
          perPage: 0,
          isBusy: false,
          tablePaginate: null,
        },
        itemSelected: null,
        detailDrawer: false,
        filtersOpen: false,
        // Reactivo al viewport real (matchMedia), nunca un string fijo:
        // BootstrapVue apaga sticky-header si `stacked` está seteado a
        // cualquier valor truthy, incluso en desktop.
        isMobileViewport: false,
        tableUsers : {
          items: [],
          fields: [
            {key: 'name', label: 'Nombre', class:"align-middle"},
            {key: 'email', label:'Email', class:"align-middle"},
            {key: 'password_visible', label:'Contraseña', class:"align-middle"},
            {key: 'roles', label:'Roles', class:"align-middle"},
            {key: 'f_action', label:'Acciones', class:"align-middle"},
          ],
        },
        tableUsersFind : {
          items: [],
          fields: [
            {key: 'name', label: 'Nombre', class:"align-middle"},
            {key: 'email', label:'Email', class:"align-middle"},
            {key: 'f_action', label:'', class:"align-middle text-right"},
          ],
        },
        arr : {
          roles: [],
          filter: {
            status:[
              {code:true, label: 'Activo'},
              {code:false, label: 'Inactivo'},
            ]
          }
        },
        filters: {
          name: '',
          status: [
            {code:true, label: 'Activo'}
          ],
        },
        crud: {
          form: {
            id: 0,
            name: '',
            phone: '',
            email: '',
            image: [],
            code: '',
            active: true,
            shipping: false,
            observations: '',
          },
          formUser: {
            user_id: 0,
            supplier_id: 0,
            name: '',
            email: '',
            password: '',
            roles: [],
            typePassword: 'password',
            iconPassword: 'visibility',
          },
          formUserFind: {
            supplier_id: 0,
            email: '',
          },
          password: {
            name: '',
            email: '',
            password: '',
          },
          formImport: {
            file: '',
            type: ''
          }
        },
        modal: {
          form: {
            active: false,
            title: ''
          },
          formUser: {
            active: false,
            title: ''
          },
          formUserRoles: {
            active: false,
            title: ''
          },
          formUserFind: {
            active: false,
            title: '',
            alertResult: false,
          },
          password: {
            active: false,
            title: ''
          },
          formImport: {
            active: false,
            title: ''
          }
        }
      }
    },
    computed: {
      currentSupplierIndex() {
        if(!this.itemSelected || !this.table.items) {
          return -1
        }
        return this.table.items.findIndex(i => i.id === this.itemSelected.id)
      },
      canGoPrevSupplier() {
        return this.currentSupplierIndex > 0
      },
      canGoNextSupplier() {
        if(this.currentSupplierIndex < 0) {
          return false
        }
        return this.currentSupplierIndex < this.table.items.length - 1
      },
    },
    watch: {
      filtersOpen: function (value) {
        StorageLocal.setValue('filters_open_crud_suppliers', value ? 'true' : 'false')
      },
      isMobileViewport() {
        this.setFieldTableSuppliers()
      },
    },
    created () {
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      this.access.elements.editSuppliers = Helper.hasAccessElement(this.access, 'edit_suppliers')
      this.access.elements.deleteSuppliers = Helper.hasAccessElement(this.access, 'delete_suppliers')
      /* Fin configuracion */
    },
    mounted() {
      this.filtersOpen = StorageLocal.getValue('filters_open_crud_suppliers') == 'true'
      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)

      this.setFieldTableSuppliers()
      this.filterSuppliers()
      this.cargarRoles()

      if(this.$route.query &&
         this.$route.query.add &&
         this.$route.query.add == 'open') {

        this.add()
        // Limpia el query apenas se consume -- si no, la pestaña queda con
        // ?add=open pegado para siempre y volver a ella (activated,
        // keep-alive) reabriría el alta de nuevo aunque el usuario solo
        // quiera ver el listado.
        this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
      }

      this._start = true
    },
    beforeDestroy() {
      if(this.mobileMql) {
        this.mobileMql.removeEventListener('change', this.updateMobileViewport)
      }
    },
    // La pestaña queda con keep-alive (ver DefaultTabsLayout.vue): si ya
    // estaba montada, "mounted" no vuelve a correr al volver a ella, así que
    // la acción "Cargar nuevo proveedor" de la paleta de comandos no
    // abriría el alta una segunda vez sin repetir acá el mismo chequeo.
    activated() {
      if(this._start) {
        this._start = false
        return false
      }

      if(this.$route.query &&
         this.$route.query.add &&
         this.$route.query.add == 'open') {

        this.add()
        // Limpia el query apenas se consume -- si no, la pestaña queda con
        // ?add=open pegado para siempre y volver a ella (activated,
        // keep-alive) reabriría el alta de nuevo aunque el usuario solo
        // quiera ver el listado.
        this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
      }
    },
    methods: {
      // Tabla Proveedores
      setFieldTableSuppliers() {
        this.table.fields = []

        if(this.parameters.codeSuppliers) {
          this.table.fields.push({key: 'code', label: 'Código', sortable: true, class:"align-middle", width:"70px"})
        } else {
          this.table.fields.push({key: 'id', label: 'ID', sortable: true, class:"align-middle", width:"50px"})
        }

        this.table.fields.push({key: 'image', label: 'Logo', class:"align-middle", width:"50px"})
        this.table.fields.push({key: 'name', label: 'Nombre', class:"align-middle", width:"200px"})
        this.table.fields.push({key: 'phone', label: 'Teléfono', class:"align-middle", width:"150px"})
        this.table.fields.push({key: 'email', label: 'Email', class:"align-middle", width:"200px"})
        this.table.fields.push({key: 'f_users', label:'Usuarios', class:"align-middle", width:"200px"})
        this.table.fields.push({key: 'active', label:'Activo', class:"align-middle " + this.alignClass('text-center'), width:"100px"})
        this.table.fields.push({key: 'shipping', label:'Transporte', class:"align-middle " + this.alignClass('text-center'), width:"100px"})

        if(!this.isMobileViewport) {
          this.table.fields.push({key: 'f_action', label:'', class:"align-middle", width:"100px"})
        }
      },
      alignClass(desktopClass) {
        return this.isMobileViewport ? 'text-left' : desktopClass
      },
      avatarFromName(name) {
        return Funciones.avatarFromName(name)
      },
      usersTooltip(users) {
        if(!users || !users.length) {
          return 'Sin usuarios asignados'
        }
        var names = users.map(u => u.name)
        if(names.length > 5) {
          return names.slice(0, 5).join(', ') + ' y ' + (names.length - 5) + ' más'
        }
        return names.join(', ')
      },
      updateMobileViewport(e) {
        this.isMobileViewport = e.matches
      },
      confirmDialog(message, options) {
        var danger = !!options.danger
        var icon = options.icon || (danger ? 'warning' : 'help')
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },

      // Administracion proveedor
      filterSuppliers (forceOpenSub=false) {
        this.table.isBusy = true
        var result = serviceAPI.filtrarProveedor(this.filters, this.table.currentPage)

        result.then((response) => {
          var data = response.data
          this.table.tablePaginate = data

          this.table.items = data.data

          this.table.items.forEach(element => {
            if(forceOpenSub && this.itemSelected && element.id == this.itemSelected.id) {
              this.openSub(element)
            }
          })

          this.table.isBusy = false
        })
        .catch(error => {
          this.table.isBusy = false
          this.$awn.alert(Error.showError(error))
        });
      },
      onRowClicked(item, index, event) {
        if(!item) return

        if(event && event.target) {
          const ignoreTags = ['INPUT', 'BUTTON', 'A', 'SELECT', 'TEXTAREA', 'LABEL', 'SVG', 'PATH']
          if(ignoreTags.includes(event.target.tagName)) return
          if(event.target.closest('.dropdown')) return
        }

        this.openSub(item)
      },
      // Resalta en la tabla la fila del registro que está abierto en el
      // drawer, para no perder la referencia visual mientras se navega con
      // los botones anterior/siguiente.
      rowClass(item, type) {
        if(type !== 'row' || !item) return ''
        if(this.detailDrawer && this.itemSelected && item.id === this.itemSelected.id) {
          return 'app-table-row-active'
        }
        return ''
      },
      openSub(item) {
        this.itemSelected = item
        this.tableUsers.items = item.users || []

        this.crud.formUser.supplier_id = item.id
        this.crud.formUserFind.supplier_id = item.id
        this.crud.formUser.typePassword = 'text'
        this.changeTypePassword()

        this.detailDrawer = true
      },
      hideSub() {
        this.itemSelected = null
        this.tableUsers.items = []
        this.detailDrawer = false
      },
      // Anterior/siguiente desde el drawer: reusa openSub() con el vecino,
      // así el contenido y el resaltado de fila se actualizan por el mismo
      // camino que un click directo en la tabla.
      goPrevSupplier() {
        if(!this.canGoPrevSupplier) return
        var item = this.table.items[this.currentSupplierIndex - 1]
        if(item) this.openSub(item)
      },
      goNextSupplier() {
        if(!this.canGoNextSupplier) return
        var item = this.table.items[this.currentSupplierIndex + 1]
        if(item) this.openSub(item)
      },
      changeTypePassword() {
        if (this.crud.formUser.typePassword === 'password') {
          this.crud.formUser.typePassword = 'text'
          this.crud.formUser.iconPassword = 'visibility_off'
        } else {
          this.crud.formUser.typePassword = 'password'
          this.crud.formUser.iconPassword  = 'visibility'
        }
      },
      cargarRoles () {
        var result = serviceAPI.obtenerRoles()

        result.then((response) => {
          var data = response.data

          data.forEach(element => {
            if( element.profile_id == Profiles.PROVEEDOR ) {
              this.arr.roles.push({ value: element.id, text: element.name })
            }
          })

        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        });
      },
      add() {
        this.crud.form.id = 0
        this.crud.form.name = ''
        this.crud.form.phone = ''
        this.crud.form.email = ''
        this.crud.form.image = []
        this.crud.form.code = ''
        this.crud.form.active = true
        this.crud.form.shipping = false
        this.crud.form.observations = ''

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = "Nuevo Proveedor"
        this.modal.form.active = true
      },
      edit(item) {
        this.crud.form.id = item.id
        this.crud.form.name = item.name
        this.crud.form.phone = (item.phone!=null) ? item.phone : ''
        this.crud.form.email = (item.email!=null) ? item.email : ''
        this.crud.form.image = item.image
        this.crud.form.code = (item.code!=null) ? item.code : ''
        this.crud.form.active = item.active
        this.crud.form.shipping = item.shipping
        this.crud.form.observations = (item.observations!=null && item.observations!=='null') ? item.observations : ''

        if(item.image.length>0) {
          this.crud.form.image = [{ path: item.image }]
        } else {
          this.crud.form.image = []
        }

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = "Editar Proveedor --> (" + this.crud.form.id + ")" + this.crud.form.name
        this.modal.form.active = true
      },
      remove(item) {
        this.crud.form.id = item.id
        this.crud.form.name = item.name
        this.crud.form.user_id = 0

        this.confirmDialog('¿Desea borrar el item (' + this.crud.form.id + ') - '+ this.crud.form.name + '?', {
          title: 'Borrar Proveedor',
          icon: 'delete',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.delete()
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })
      },
      save() {
        // valida que tenga el formato correcto para emails
        if(this.crud.form.email) {
          var expreg = new RegExp("^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$")
          if (!expreg.test(this.crud.form.email)) {
            this.$awn.alert("El email no posee un formato válido")
            return false
          }
        }

        // valida que tenga el formato para whatsapp
        if(this.crud.form.phone) {
          if(!Funciones.validarTelefonoWhatsapp(this.crud.form.phone)) {
            this.$awn.alert("El nro de télefono no posee un formato válido para Whatsapp")
            return false
          }
        }

        let loader = this.$loading.show();

        var image = new FormData();
        this.crud.form.image.forEach((value, key) => {
          image.append(key, value.path);
        })

        if (this.crud.form.id) {
          var result = serviceAPI.editar(JSON.stringify(this.crud.form), image);
        } else {
          var result = serviceAPI.agregar(JSON.stringify(this.crud.form), image);
        }

        result.then((response) => {
          this.modal.form.active = false
          loader.hide()
          this.filterSuppliers(true)
          this.$awn.success("Datos guardados con éxito");

          if (!this.crud.form.id) {
            this.goSuppliersView({
              id:response.data.id
            })
          }
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error));
        })
      },
      delete() {
        let loader = this.$loading.show();
        var result = serviceAPI.eliminar(this.crud.form.id);

        result.then((response) => {
          this.modal.formUser.active = false
          if(this.itemSelected && this.itemSelected.id === this.crud.form.id) {
            this.hideSub()
          }
          loader.hide()
          this.filterSuppliers()
          this.$awn.success("Registro eliminado");
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error));
        })
      },

      // Import By CSV
      openImportCSV() {
        this.crud.formImport.type = 'import_store_suppliers'
        this.crud.formImport.file = ''

        this.modal.formImport._id = Math.random().toString(36).slice(2, 8)
        this.modal.formImport.title = "Gestor de Importaciones"
        this.modal.formImport.active = true
      },
      importCSV() {
        this.confirmDialog('¿Continuar con la importación?', {
          title: 'Importar desde CSV',
          icon: 'file-earmark-spreadsheet',
          okTitle: 'IMPORTAR'
        })
        .then(value => {
          if (value) {
            let loader = this.$loading.show();

            var file = new FormData();
            file.append("file", this.crud.formImport.file);

            var result = serviceAPI.importarProveedores(JSON.stringify(this.crud.formImport), file);

            result.then((response) => {
              this.modal.formImport.active = false
              loader.hide()
              this.filterSuppliers()
              this.$awn.success("Archivo importado con éxito");
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })
      },
      goDownloadFormat(value) {
        var win = window.open(Helper.getPathPublic(value), '_blank');
        win.focus();
      },

      // Administracion usuario
      filtroProveedor(row) {
        var arr = []
        row.forEach(element => {
          if(element.profile_id == Profiles.PROVEEDOR) {
            arr.push(element)
          }
        })
        return arr
      },
      createUser (item) {
        this.crud.formUser.supplier_id = item.id
        this.crud.formUser.user_id = 0
        this.crud.formUser.name = ''
        this.crud.formUser.email = ''
        this.crud.formUser.password = ''
        this.crud.formUser.roles = []
        this.modal.formUser.title = "Asignar Usuario"
        this.modal.formUser.active = true
      },
      editRolesUser(item) {
        // this.crud.formUser.supplier_id cargado desde el this.openSub()
        this.crud.formUser.user_id = item.id
        this.crud.formUser.name = item.name
        this.crud.formUser.email = item.email
        this.crud.formUser.password = ''
        this.crud.formUser.roles = []

        if(item.roles) [
          item.roles.forEach(element => {
            this.crud.formUser.roles.push(element.id)
          })
        ]

        this.modal.formUserRoles.title = "Editar Roles Usuario --> " + this.crud.formUser.name + " - " + this.crud.formUser.email
        this.modal.formUserRoles.active = true
      },
      removeUser(item) {
        // this.crud.formUser.supplier_id cargado desde el this.openSub()
        this.crud.formUser.user_id = item.id
        this.crud.formUser.name = item.name
        this.crud.formUser.email = item.email
        this.crud.formUser.password = ''
        this.crud.formUser.roles = []

        this.confirmDialog('¿Desea borrar el usuario (' + this.crud.formUser.name + " - " + this.crud.formUser.email + ')?', {
          title: 'Borrar Usuario',
          icon: 'delete',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.deleteUser()
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })
      },
      saveUser() {
        let loader = this.$loading.show();
        var result = serviceAPI.crearUsuario(this.crud.formUser);

        result.then((response) => {
          this.modal.formUser.active = false
          loader.hide()
          this.filterSuppliers(true)
          this.$awn.success("Usuario creado con éxito");
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error));
        })
      },
      saveUserRoles() {
        let loader = this.$loading.show();
        var result = serviceAPI.editarUsuarioRoles(this.crud.formUser);

        result.then((response) => {
          this.modal.formUserRoles.active = false
          loader.hide()
          this.filterSuppliers(true)
          this.$awn.success("Roles editados con éxito");
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error));
        })
      },
      deleteUser() {
        let loader = this.$loading.show();
        var result = serviceAPI.eliminarUsuario(this.crud.formUser);

        result.then((response) => {
          loader.hide()
          this.filterSuppliers(true)
          this.$awn.success("Usuario desvinculado con éxito");
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error));
        })
      },

      // Buscar un usuario
      openFindUser (item) {
        this.crud.formUser.supplier_id = item.id
        this.crud.formUserFind.email = ''
        this.modal.formUserFind.alertResult = false
        this.tableUsersFind.items = []

        this.modal.formUserFind.title = "Buscar Usuario"
        this.modal.formUserFind.active = true
      },
      findUser () {
        let loader = this.$loading.show();
        var result = serviceAPI.seleccionarUsuario(this.crud.formUserFind)

        result.then((response) => {
          var data = response.data
          this.tableUsersFind.items = data

          if(data.length) {
            this.modal.formUserFind.alertResult = false
          } else {
            this.modal.formUserFind.alertResult = true
          }

          loader.hide()
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
          loader.hide()
        });
      },
      selectUser (item) {
        let loader = this.$loading.show();

        var user = {  user_id: item.id,
                      supplier_id: this.crud.formUser.supplier_id }

        var result = serviceAPI.vincularUsuario(user);

        result.then((response) => {
          this.modal.formUserFind.active = false
          loader.hide()
          this.filterSuppliers(true)
          this.$awn.success("Usuario creado con éxito");
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error));
        })
      },
      viewAccess(item){
        this.crud.password.name = item.name
        this.crud.password.email = item.email
        this.crud.password.password = item.password_visible

        this.modal.password.title = "Accesos --> " + this.crud.password.name
        this.modal.password.active = true
      },

      // Detalle view proveedor
      goSuppliersView(item) {
        this.$router.push({ name: 'SuppliersViewStaff', params: {suppliersID: item.id} })
      }
    }
  }
</script>

<style>
  .badge-module-suppliers-custom {
    font-size: 14px;
  }
</style>
<style scoped>
  .b-avatar {
    width: 25px;
    height: 25px;
  }
</style>
